import React, { useReducer } from 'react';

import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PAGE_NAMES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import OwnerStory from 'dpl/components/OwnerStory';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';

import { usePuppyData } from '../hooks/usePuppyData';
import Section from './Section';

const IS_GT_MD = isGreaterThanBreakpoint('md');
const COLLAPSED_STORIES_COUNT = 6;

export default function OwnerStories() {
  const { ownerStories } = usePuppyData();

  const { total_count: count, description } = ownerStories;
  const isCollapsible = count > COLLAPSED_STORIES_COUNT;

  const [isCollapsed, toggleIsCollapsed] = useReducer(
    collapsed => !collapsed,
    isCollapsible
  );

  const items = isCollapsed
    ? ownerStories.items.slice(0, COLLAPSED_STORIES_COUNT)
    : ownerStories.items;

  const columns = items.reduce(
    (acc, story, index) => {
      const key = IS_GT_MD ? index % 2 : 0;
      acc[key].push(story);
      return acc;
    },
    [[], []]
  );

  function handleClick() {
    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: isCollapsed ? 'show_all_reviews' : 'show_less_reviews',
      view: ANALYTICS_VIEWS.PUPPY_PROFILE,
      view_feature_location:
        ANALYTICS_VIEW_FEATURE_LOCATIONS.OWNER_STORIES_SECTION
    });

    toggleIsCollapsed();
  }

  if (count === 0) return null;

  return (
    <SectionViewTracker
      name={ANALYTICS_PAGE_NAMES.PUPPY_PROFILE}
      category="puppy-profile-owner-stories"
      threshold={0.5}
    >
      <Section
        className="OwnerStories bg-subtle-light-gray"
        id="ownerStoriesSection"
      >
        <Section.Heading>
          {count} verified owner {count === 1 ? 'story' : 'stories'}
        </Section.Heading>
        <Section.Subheading className="mt2 mt4-md">
          {description}
        </Section.Subheading>
        <div className="row justify-between">
          {columns.map((stories, index) => (
            <div key={index} className="col-lg-6" style={{ maxWidth: 520 }}>
              {stories.map(story => (
                <OwnerStory
                  key={story.id}
                  className="mt12"
                  date={story.short_date}
                  description={story.description}
                  gallery={story.gallery}
                  user={story.owner}
                />
              ))}
            </div>
          ))}
        </div>
        {isCollapsible && (
          <button
            type="button"
            className="font-16 fw-medium pv3 ph10 mt12 br2 ba b--stone-700 stone-700"
            onClick={handleClick}
          >
            {isCollapsed ? `Show all ${count} reviews` : 'Show less reviews'}
          </button>
        )}
      </Section>
    </SectionViewTracker>
  );
}
